// TODO(RBAC, parent #641 #647): 権限チェックは別途実装
import { useEffect, useMemo, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { CreateWorkflowCasesRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import type { WorkflowCaseTag } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Box,
  type BoxProps,
  FieldErrorText as EcFieldErrorText,
  FieldLabel as EcFieldLabel,
  FieldRoot,
  HStack,
  Link,
  Spinner,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { DsMultiSelect } from '~/components/common/DsMultiSelect'
import { FormSelect } from '~/components/common/FormSelect'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { WorkflowCaseTagEditor } from '~/components/common/WorkflowCaseTagEditor'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useListWorkflowTagPool } from '~/hooks/workflowCase/useListWorkflowTagPool'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { convertAreaLabel } from '~/models/area'
import { formatDateValue, parseIsoDate, toProtoDate } from '~/models/dateTime'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { useListWorkflowTemplates } from '~/pages/AdminWorkflowTemplates/hooks/useListWorkflowTemplates'
import { TargetDateFields } from '~/pages/WorkflowCases/components/TargetDateFields'
import type { CreateCasesFormValues } from '~/pages/WorkflowCases/forms/createCaseSchema'
import { createCasesSchema } from '~/pages/WorkflowCases/forms/createCaseSchema'
import {
  extractConflictDetails,
  useCreateWorkflowCases,
} from '~/pages/WorkflowCases/hooks/useCreateWorkflowCases'
import { useListWorkflowGenerationTargetCandidates } from '~/pages/WorkflowCases/hooks/useListWorkflowGenerationTargetCandidates'
import {
  buildWorkflowTargetCandidates,
  candidateNameOn,
  filterBgCandidates,
  hasExpiredSelectedBg,
} from '~/pages/WorkflowCases/models/bgCandidates'
import { buildConflictMessage } from '~/pages/WorkflowCases/models/conflictMessage'
import { resolveTemplateRequiresBgMessage } from '~/pages/WorkflowCases/models/serverError'
import { workflowTargetCodeKindLabelKey } from '~/pages/WorkflowCases/models/workflowTargetCodeKind'
import { routePaths } from '~/routes/routePaths'

// chakra v3.29 + React 18 types の既知不具合で `FieldLabelProps` / `FieldErrorTextProps`
// が `{ unstyled }` のみに collapse し children を JSX で受け取れなくなるため、
// ecloud 由来の component を BoxProps で型付けし直す。 `htmlFor` は label ↔ input の
// 明示的な関連付けに使う (= chakra v3 Field の auto id 共有が ecloud Input まで
// 伝播せず getByRole の accessible name が切れるため、 input 側 `id` と一致させる)。
const FieldLabel = EcFieldLabel as React.FC<BoxProps & { htmlFor?: string }>
const FieldErrorText = EcFieldErrorText as React.FC<BoxProps>

type Props = {
  open: boolean
  onClose: () => void
}

/**
 * 必須マーク (赤 `*`)。 design (case-new-modal) の `.required` 相当。
 * FieldRequiredIndicator は上記 Field collapse の影響を受けるため自前で描画する。
 */
const RequiredMark = (): React.ReactElement => (
  <Text as="span" marginLeft={1} color="Text.negativeMid">
    *
  </Text>
)

/**
 * 一括作成ダイアログ (spec #1515/AC-003b, design: demand/cases.html #case-new-modal)。
 */
export const CreateWorkflowCasesDialog = ({
  open,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // ダイアログは top ページに常時マウントされるため、雛形一覧 (最重量 payload)
  // は `open` になるまで発火させない (初期表示時の不要なサーバー往復を避ける)。
  // isLoadingTemplates は Spinner / submit 無効化に配線済みなので、開いた直後の
  // ロード中も UI 上で表現される。
  const { data: templateData, isLoading: isLoadingTemplates } =
    useListWorkflowTemplates({ enabled: open })
  // bgMasters はページ本体 (WorkflowCases/index.tsx) が無条件に取得しており
  // query key が一致するため、ここで gate しても初期表示の往復は減らない。
  const { data: bgMastersData, isLoading: isLoadingBgMasters } =
    useListBgMasters()
  const { mutateAsync: createAsync, isPending } = useCreateWorkflowCases()
  const showSuccessToast = useSuccessToast()
  // 組織全体のタグプール (spec AC-TAG-005): サジェスト表示 (AC-TAG-008) + 編集
  // トグル (AC-TAG-009) + 20 個上限事前チェック (AC-TAG-006) に使う
  // (tag-pool-limit-and-suggest design)。これらは loading state を持たず
  // Spinner も出ないため `open` gate すると開いた直後にサジェスト UI が欠落する
  // ウィンドウが生じる。それを避けるため eager 取得する。
  const { data: tagPoolData } = useListWorkflowTagPool()

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<CreateCasesFormValues>({
    resolver: zodResolver(createCasesSchema(t)),
    defaultValues: {
      // AC-01: 初期 chips は空 (雛形選択の有無に関わらず、業務ユーザーが明示的に
      // 選択して初めて登録する。spec workflow.md L385)。
      targetDates: [],
      bgCodes: [],
      // AC-03: 雛形未選択の初期状態は BG 欄を表示したままにする (= 導出は雛形を
      // 選んで初めて発火する。誤って隠さない)。
      requiresBgCode: true,
    },
  })

  const templates = useMemo(
    () => templateData?.templates ?? [],
    [templateData?.templates],
  )
  const selectedTemplateId = watch('workflowTemplateId')

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId),
    [templates, selectedTemplateId],
  )

  // 発電の計画提出者コードを候補に含めるかは backend が雛形のステップ構成から
  // 導出した `allows_generation_target_code` を読むだけにする (AC-WFW-003j)。
  const allowsGenerationTargetCode =
    selectedTemplate?.allowsGenerationTargetCode ?? false

  // 発電の候補は入札だけの雛形を選んでいる時だけ取得する。取得中・取得失敗の間は
  // 発電の候補が出ないだけにし、BG マスタの Spinner・submit 無効化・error boundary
  // には波及させない (需要BG・自己託送BG での作成を止めない)。
  const { data: generationCandidatesData } =
    useListWorkflowGenerationTargetCandidates({
      enabled: open && allowsGenerationTargetCode,
    })

  // BG コード欄の候補の母集合 (spec AC-WFW-003j)。エリア候補・絞り込み・
  // 失効警告・自動除外はすべてこの母集合を基準にする。
  const targetCandidates = useMemo(
    () =>
      buildWorkflowTargetCandidates(
        bgMastersData?.items ?? [],
        allowsGenerationTargetCode
          ? (generationCandidatesData?.targets ?? [])
          : [],
      ),
    [
      bgMastersData?.items,
      allowsGenerationTargetCode,
      generationCandidatesData?.targets,
    ],
  )
  const targetDates = watch('targetDates')
  // TargetDateFields は Date[] を扱う controlled component。 SoT は RHF
  // `targetDates` (ISO) のままにし、 component 境界で変換する (design doc §6)。
  const targetDateObjects = useMemo(
    () =>
      targetDates
        .map((d) => parseIsoDate(d))
        .filter((d): d is Date => d !== undefined),
    [targetDates],
  )
  const selectedBgCodes = watch('bgCodes')

  // --- エリアフィルタ (spec AC-WFW-003: BG 必須モードのみ表示、BG 候補の絞り込み専用) ---
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<AreaCode[]>([])

  // 候補が実在するエリア。BG 必須モードでは「どのエリアの候補に絞り込むか」の
  // 選択肢になるので、候補を持たないエリアを出しても選ぶ意味が無い。
  const availableAreaCodes = useMemo(() => {
    const areaCodes = new Set(targetCandidates.map((c) => c.areaCode))
    return [...areaCodes]
      .filter((code) => code !== AreaCode.UNSPECIFIED)
      .sort((a, b) => a - b)
  }, [targetCandidates])

  // issue #5219 Decisions #8 (spec AC-WFW-003 文言): エリア未選択時は全 BG が
  // 候補になる (= 旧 spec PR #1712 の「未選択時は空」挙動を撤回)。対象日の
  // いずれか 1 日で有効な BG のみに絞る (対象日未選択時は today 基準、
  // filterBgCandidates の既定引数)。
  // today を caller 側で生成して useMemo 依存に含めると、生成する CivilDate
  // オブジェクトが毎 render 新しい参照になり memo が無効化されるため、
  // 既定引数に委ねて依存配列から外す。
  const filteredTargetCandidates = useMemo(
    () =>
      filterBgCandidates(
        targetCandidates,
        selectedAreaCodes,
        targetDateObjects,
      ),
    [targetCandidates, selectedAreaCodes, targetDateObjects],
  )

  // 選択済み BG が候補日の全てで失効しているかの警告 (submit は止めない。
  // backend の全日有効チェックが最終 gate、issue #5219 Decisions #8)。
  const hasExpiredBgSelected = hasExpiredSelectedBg(
    selectedBgCodes,
    targetCandidates,
    targetDateObjects,
  )

  // エリア変更時: 選択済み BG から該当しなくなった BG を自動除外する。
  // エリアが非空の時だけ行う (エリア全解除は「全 BG が候補」に戻るだけなので
  // 除外しない。issue #5219 Decisions #8 — 現行の全消去 effect は撤去)。
  useEffect(() => {
    if (selectedAreaCodes.length === 0) return
    const validCodes = new Set(
      targetCandidates
        .filter((c) => selectedAreaCodes.includes(c.areaCode))
        .map((c) => c.code),
    )
    const current = watch('bgCodes')
    const filtered = current.filter((code) => validCodes.has(code))
    if (filtered.length !== current.length) {
      setValue('bgCodes', filtered)
    }
  }, [selectedAreaCodes, targetCandidates, watch, setValue])

  // 雛形の切替で母集合から外れたコード (例: 発電の計画提出者コードを選んだまま
  // 需要想定を含む雛形へ切り替えた時) を知らせずに選択から外す (spec AC-WFW-003j)。
  // 対象日での失効は母集合を変えないため、ここでは外さない (警告だけ出す)。
  useEffect(() => {
    const candidateCodes = new Set(targetCandidates.map((c) => c.code))
    const current = watch('bgCodes')
    const kept = current.filter((code) => candidateCodes.has(code))
    if (kept.length !== current.length) {
      setValue('bgCodes', kept)
    }
  }, [targetCandidates, watch, setValue])

  // BG コードの要否は backend が雛形のステップ構成から導出した derived field
  // (`requires_bg_code`) をそのまま読む。frontend は「どのリンク先画面が BG を
  // 要するか」(AC-WFW-003j) を知らない (= 判定の単一 SoT は backend、
  // workflow-bg-code-derivation design)。雛形未選択の間は true 扱いにして BG 欄を
  // 表示したままにする (AC-03)。
  const requiresBgCode = selectedTemplate?.requiresBgCode ?? true

  // 特異日注記のエリア絞り込み。 BG 必須時は選択 BG のエリアだけ (PR #5890
  // レビュー、東京・関西をエリア選択し東京 BG だけ選ぶと関西限定の特異日まで
  // 注記されるのを防ぐ)。 BG 不要モードはエリアという概念が無くなった
  // (issue #5219) ため、全エリア共通分の特異日注記のみを対象にする (空配列)。
  const singularNoteAreaCodes = useMemo(() => {
    if (!requiresBgCode) return []
    const codes = new Set<AreaCode>()
    for (const c of targetCandidates) {
      if (
        selectedBgCodes.includes(c.code) &&
        c.areaCode !== AreaCode.UNSPECIFIED
      ) {
        codes.add(c.areaCode)
      }
    }
    return [...codes]
  }, [requiresBgCode, selectedBgCodes, targetCandidates])

  // schema 側 (createCasesSchema の superRefine) も BG 要否で分岐するため、
  // 導出値をフォーム状態へ写す。
  useEffect(() => {
    setValue('requiresBgCode', requiresBgCode)
  }, [requiresBgCode, setValue])

  // AC-12: モードが切り替わった時だけ BG 選択を捨てる。エリア選択は BG 必須
  // モードでのみ編集可能な欄なので (issue #5219: BG 不要モードは欄自体を
  // 非表示にする)、モード切替時にエリア候補を narrow するロジックは不要になった
  // (「BG 不要 → BG 必須」切替時のエリア選択維持ロジックは撤去、Decisions #7)。
  const prevRequiresBgCodeRef = useRef(requiresBgCode)
  useEffect(() => {
    if (prevRequiresBgCodeRef.current === requiresBgCode) return
    prevRequiresBgCodeRef.current = requiresBgCode
    setValue('bgCodes', [])
  }, [requiresBgCode, setValue])

  // AC-TAG-002: タグ初期値 = 選択雛形の default_tags のみ（0 個ならタグ 0 個）。
  // react-hook-form の schema には含めない (selectedAreaCodes と同じく、
  // proto message を直接保持する複雑な値のため useState で管理する)。
  const [tags, setTags] = useState<WorkflowCaseTag[]>([])
  // 対象日チップと同様、「ユーザーが未編集」の間だけ雛形選択に追従させる。
  const tagsAutoManagedRef = useRef(true)
  // AC-TAG-009 (workflow-tag-delete-ui design): 「作成済のタグ」ボックスの
  // 編集モードで削除予定にした label の集合。保存時に removed_tag_labels
  // として送出する (Decision A/C: 同一トランザクションで作成 + org 横断削除)。
  const [removedTagLabels, setRemovedTagLabels] = useState<string[]>([])

  useEffect(() => {
    if (selectedTemplateId === '' || selectedTemplateId === undefined) return
    const tpl = templates.find((t) => t.id === selectedTemplateId)
    if (tpl === undefined) return
    // AC-TAG-002: タグは未編集の間は雛形選択に追従させる。default_tags が
    // 0 個ならタグ 0 個（フォールバックなし）。 対象日 chip の自動追加は撤去済み
    // (spec workflow.md L385: 初期状態は chips 空、業務ユーザーが明示的に選択して
    // 初めて登録する。design doc 4016-workflow-target-date-multi-select)。
    if (tagsAutoManagedRef.current) {
      setTags(tpl.defaultTags)
      // AC-TAG-009 (Decision D の不変条件復元): 削除予定は未付与プールタグだけが
      // 対象。切替先雛形の default_tags に削除予定 label が含まれると付与済みに
      // なるため、削除予定に残すと保存時に backend の removed 優先で無言に剥がれる。
      // 付与される label は削除予定から外して整合させる。
      setRemovedTagLabels((prev) =>
        prev.filter((l) => !tpl.defaultTags.some((tag) => tag.label === l)),
      )
    }
  }, [selectedTemplateId, templates])

  // AC-04 / AC-05 (issue #5219): 作成件数は作成軸に依る。BG 必須モードは
  // BG 数 × 対象日数 (エリアは絞り込みでしかない)、BG 不要モードは対象日数のみ
  // (エリアは作成軸から除去された)。
  const createCount = requiresBgCode
    ? selectedBgCodes.length * targetDates.length
    : targetDates.length

  const handleClose = (): void => {
    reset()
    setSelectedAreaCodes([])
    setTags([])
    setRemovedTagLabels([])
    tagsAutoManagedRef.current = true
    prevRequiresBgCodeRef.current = true
    onClose()
  }

  const onSubmit = async (values: CreateCasesFormValues): Promise<void> => {
    try {
      await createAsync(
        create(CreateWorkflowCasesRequestSchema, {
          workflowTemplateId: values.workflowTemplateId,
          // 作成軸は oneof。BG 必須雛形は BG 軸、BG 不要雛形は no_bg_target
          // (issue #5219: エリア軸は廃止。AC-12: 切替後は BG を一切送らない)。
          target: requiresBgCode
            ? {
                case: 'bgTargets',
                value: { bgCodes: values.bgCodes },
              }
            : {
                case: 'noBgTarget',
                value: {},
              },
          targetDates: values.targetDates
            .map((d) => parseIsoDate(d))
            .filter((d): d is Date => d !== undefined)
            .map(toProtoDate),
          tags,
          removedTagLabels,
        }),
      )
      /*
       * 作成できたことと作成軸の内訳を toast で伝える (デザイナー指摘
       * 2026-08-28「追加後に成功 toast を出したい」「今作成したワークフローに
       * 気づきたい」)。 design `demand/cases.html` の作成完了 toast と同じ形。
       *
       * design の文言は「一覧の先頭に追加しました」 だが、 一覧の並び替え
       * (`sortBy` / `sortOrder`) と絞り込み (`FilterBar`) はユーザーが変えられる
       * ため、 位置を保証する文言は事実と食い違いうる (レビュー指摘 + デザイナー
       * 判断 2026-08-28 で位置の主張を落とす方針に確定)。 既定ソートが最終更新
       * 降順 (AC-WFW-001) である性質は変えていないので、 既定のまま使っている
       * 限り作成直後のワークフローは先頭に来る。
       */
      showSuccessToast({
        title: t('page:workflowCases.create.successTitle', {
          count: createCount,
        }),
        description: requiresBgCode
          ? t('page:workflowCases.create.successDescBg', {
              bgCount: values.bgCodes.length,
              dateCount: values.targetDates.length,
            })
          : t('page:workflowCases.create.successDescNoBg', {
              dateCount: values.targetDates.length,
            }),
        // DS の ToastComponent は `closable === true` の時だけ CloseTrigger を
        // 描画する。 8080 の該当 toast は `isClosable: true` (design
        // `demand/cases.html:2642`) で、 本 toast は説明文付きの 2 行なので
        // 既定の 5000ms で消える前に読み切れないことがある。
        // 全 toast を × 付きにするか (プロトは 201 箇所中 200 が × あり、
        // 実装は 74 箇所中 1 箇所のみ) は #6689 で Des 判断待ち。
        closable: true,
      })
      handleClose()
    } catch (err) {
      if (err instanceof ConnectError) {
        switch (err.code) {
          case Code.FailedPrecondition: {
            const conflicts = extractConflictDetails(err)
            if (conflicts.length > 0) {
              setError('root.serverError', {
                message: buildConflictMessage(conflicts, t),
              })
            } else {
              // 衝突以外の拒否 (新規作成で作れない計画断面の雛形等) は画面の操作
              // では起きないため、 通信エラー・サーバーエラーと同じ文言にする
              // (spec AC-WFW-003r)。
              setError('root.serverError', {
                message: t('page:workflowCases.serverError.createFailed'),
              })
            }
            break
          }
          case Code.InvalidArgument: {
            // issue #5219 Decisions #13/#15: BG 不要モードで承認対象の業務処理
            // に紐づくサブステップを持つ雛形を選ぶと backend が typed ErrorInfo
            // 付きで拒否する。専用文言に解決できなければ AC-WFW-003r の
            // 入力の誤りの文言にフォールバックする。
            const templateRequiresBgMessage = resolveTemplateRequiresBgMessage(
              err,
              t,
            )
            setError('root.serverError', {
              message:
                templateRequiresBgMessage ??
                t('page:workflowCases.serverError.invalidArgument'),
            })
            break
          }
          // AC-TAG-006 (tag-pool-limit-and-suggest design): frontend の 20 個
          // 上限事前チェック (WorkflowCaseTagEditor) は同一セッション内の複数
          // 追加やプール未取得中はすり抜けうる。backend の最終判定
          // (ResourceExhausted) を固定文言へ写像し、汎用 createFailed に
          // 落ちて無言 fail する事故を防ぐ。
          case Code.ResourceExhausted:
            setError('root.serverError', {
              message: t('common:tag.poolLimitError'),
            })
            break
          default:
            setError('root.serverError', {
              message: t('page:workflowCases.serverError.createFailed'),
            })
        }
      } else {
        setError('root.serverError', {
          message: t('page:workflowCases.serverError.createFailed'),
        })
      }
    }
  }

  const handleSave = (): void => {
    void handleSubmit(onSubmit, (formErrors) => {
      // 項目脇に FieldErrorText が出るエラーは alert に再掲しない (重複表示になる)。
      const alertMessage = resolveFormErrorAlertMessage(
        formErrors,
        t('page:workflowCases.serverError.validation'),
      )
      if (alertMessage !== undefined) {
        setError('root.serverError', { message: alertMessage })
      } else {
        // 直前の失敗で出した alert が残らないよう明示的に消す
        // (項目脇の表示だけに切り替わるため)。
        clearErrors('root.serverError')
      }
    })()
  }

  // 対象日 0 件では非活性にせず、 押した時に schema の min(1) で対象日入力に
  // エラーを出す (spec workflow.md §1 機能詳細「対象日入力」)。
  // BG 必須なのに BG 未選択のままでは作成を実行できない (= TC-V-05.1 の
  // 「作成が中止される」はこの disabled が担う)。
  const isSubmitDisabled =
    isLoadingTemplates ||
    isLoadingBgMasters ||
    (requiresBgCode && selectedBgCodes.length === 0)

  // 排他衝突 (AC-003a) の文は 1 行目が見出し、 2 行目以降が衝突箇所の箇条書き
  // (buildConflictMessage)。 1 行目を Alert の title、 残りを description に渡す。
  const [serverErrorTitle, ...serverErrorRestLines] =
    errors.root?.serverError?.message?.split('\n') ?? []

  const footer = (
    <SaveCancelFooter
      isPending={isPending}
      isSaveDisabled={isSubmitDisabled}
      onCancel={handleClose}
      onSave={handleSave}
      saveLabel={t('page:workflowCases.create.submit')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={open}
      onClose={handleClose}
      size="md"
      title={t('page:workflowCases.create.title')}
    >
      <VStack align="stretch" gap={3} width="100%">
        {/* 雛形選択 (+ 雛形管理リンク) */}
        <FieldRoot
          gap={1}
          invalid={errors.workflowTemplateId !== undefined}
          required
        >
          <HStack align="center" justify="space-between" width="100%">
            {/* textStyle="label" = md/semibold で意図的視覚変更 (12px→14px)。
                design/css/components.css:1250-1256 `.form-label` = md/600 が正、
                ユーザー承認済み (本 modal の 5 ラベル共通)。 */}
            {/* htmlFor は指定しない: DS Select の内部 input id は FieldRoot の
                field context 由来 (= FieldLabel が自動で for を張る)。 */}
            <FieldLabel textStyle="label" marginBottom={0}>
              {t('page:workflowCases.form.workflowTemplateId')}
              <RequiredMark />
            </FieldLabel>
            {/* クリックで遷移する導線 (gear icon + テキスト) なので DS Link に統一
                (色 = Text.accentPrimary / hover 下線 / focus ring は DS 既定に委譲。
                design `.ecl-text-link` と同値のため色指定は持たない)。
                gear icon は currentColor 継承でリンク色に追随する。
                雛形管理は別タブで開く。 2026-10-16 のデザイナー決定 (別途指定の無い
                リンクは別タブで開かない) の下での別タブ指定で、 理由は編集中の内容を
                失わないため (このダイアログに離脱確認が無い)。 2026-10-16 Des レーン決定。
                内部遷移なので external icon は付けない。 */}
            <Link alignItems="center" gap={1} fontSize="md" asChild>
              <RouterLink
                rel="noopener noreferrer"
                target="_blank"
                to={routePaths.adminWorkflowTemplates.path}
              >
                <FaGearRegular boxSize="14px" />
                <Text as="span">
                  {t('page:workflowCases.form.templateManageLink')}
                </Text>
              </RouterLink>
            </Link>
          </HStack>
          {isLoadingTemplates ? (
            <Spinner />
          ) : (
            <FormSelect
              // aria-label で accessible name を固定する (= e2e / F5 テストが
              // getByLabel / getByRole name で一意特定するため。背面 FilterBar の
              // select も role=combobox を持ち、role だけでは特定できない)
              width="100%"
              aria-label={t('page:workflowCases.form.workflowTemplateId')}
              control={control}
              name="workflowTemplateId"
              options={templates.map((tpl) => ({
                value: tpl.id,
                label: localizeBuiltinWorkflowText(tpl.name, t),
              }))}
              // ラベル行 (HStack width="100%") と同じ幅に敷く。 指定しないと
              // react-select の container が内容幅に縮み、 下のエリア / BG
              // (DsMultiSelect width="100%") と横幅が揃わない
              // (デザイナー指摘 2026-08-14)。
              placeholder={t(
                'page:workflowCases.form.workflowTemplateIdPlaceholder',
              )}
            />
          )}
          {/* fontSize="sm" = 12px。design/css/components.css:1282-1285
              `.form-error-msg` = var(--font-size-sm) が正。chakra field recipe の
              textStyle 'xs' (10px) が素通りするため明示する (本 modal の 3 箇所共通)。 */}
          {errors.workflowTemplateId !== undefined && (
            <FieldErrorText fontSize="sm">
              {errors.workflowTemplateId.message}
            </FieldErrorText>
          )}
        </FieldRoot>

        {/* エリア選択 (typeahead multi-select、spec AC-WFW-003)。
            issue #5219 Decisions #7: BG 必須モードのみ表示 (BG 候補の絞り込み
            専用で、BG 欄が無い BG 不要モードに残す意味が無い)。未選択でも
            全 BG が候補になるため必須項目ではない。 */}
        {requiresBgCode && (
          <FieldRoot gap={1}>
            <FieldLabel textStyle="label" htmlFor="createWorkflowCases-area">
              {t('page:workflowCases.form.area')}
            </FieldLabel>
            {isLoadingBgMasters ? (
              <Spinner />
            ) : (
              <DsMultiSelect
                width="100%"
                aria-label={t('page:workflowCases.form.area')}
                inputId="createWorkflowCases-area"
                noMatchMessage={t('page:workflowCases.form.areaNoMatch')}
                noOptionsMessage={t('page:workflowCases.form.areaEmpty')}
                onChange={(next) => {
                  setSelectedAreaCodes(
                    [...next].map((v) => Number(v) as AreaCode),
                  )
                }}
                options={availableAreaCodes.map((code) => ({
                  value: String(code),
                  label: convertAreaLabel(code),
                }))}
                placeholder={t('page:workflowCases.form.areaPlaceholder')}
                selected={new Set(selectedAreaCodes.map(String))}
              />
            )}
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:workflowCases.form.areaHintBgFilter')}
            </Text>
          </FieldRoot>
        )}

        {/* BG コード複数選択 (typeahead multi-select)。
            issue #5219 Decisions #8: エリア未選択時も全 BG が候補になる (旧
            spec PR #1712 の disabled + 候補空 挙動を撤回)。BG 候補は選択済み
            対象日のいずれか 1 日で有効な BG に絞る (対象日未選択時は today 基準)。
            AC-02: BG 不要と導出される雛形では欄ごと非表示にする (DOM から消す)。 */}
        {requiresBgCode && (
          <FieldRoot gap={1} invalid={errors.bgCodes !== undefined} required>
            <FieldLabel textStyle="label" htmlFor="createWorkflowCases-bgCodes">
              {t('page:workflowCases.form.bgCodes')}
              <RequiredMark />
            </FieldLabel>
            {isLoadingBgMasters ? (
              <Spinner />
            ) : (
              <DsMultiSelect
                width="100%"
                aria-label={t('page:workflowCases.form.bgCodes')}
                inputId="createWorkflowCases-bgCodes"
                invalid={errors.bgCodes !== undefined}
                noMatchMessage={t('page:workflowCases.form.bgCodesNoMatch')}
                noOptionsMessage={t('page:workflowCases.form.bgCodesEmpty')}
                onChange={(next) => {
                  setValue('bgCodes', [...next])
                }}
                options={filteredTargetCandidates.map((c) => {
                  // design `_bgOptionLabel` 準拠: 複数エリア選択時のみ [エリア] を接頭
                  const label = `${c.code} - ${candidateNameOn(c, targetDateObjects)} (${t(workflowTargetCodeKindLabelKey(c.kind))})`
                  return {
                    value: c.code,
                    label:
                      selectedAreaCodes.length > 1
                        ? `[${convertAreaLabel(c.areaCode)}] ${label}`
                        : label,
                  }
                })}
                placeholder={t('common:form.selectPlaceholder')}
                selected={new Set(selectedBgCodes)}
              />
            )}
            {/* BG hint 常設 (design #case-new-bg-hint: font-size: var(--font-size-sm) = 12px) */}
            <Text color="Text.midEmphasis" fontSize="sm">
              {selectedAreaCodes.length === 0
                ? t('page:workflowCases.form.bgCodesHint')
                : t('page:workflowCases.form.bgCodesHintSelected')}
            </Text>
            {/* issue #5219 Decisions #8: 全対象日で失効した BG が選択されている
                場合の非ブロッキング警告 (submit は止めない、backend が最終
                gate)。既存 form warning の作法 (`.form-warn-msg` 相当) に寄せる。 */}
            {hasExpiredBgSelected && (
              <Text color="Text.warningHigh" fontSize="sm">
                {t('page:workflowCases.form.bgCodesExpiredWarning')}
              </Text>
            )}
            {errors.bgCodes !== undefined && (
              <FieldErrorText fontSize="sm">
                {typeof errors.bgCodes.message === 'string'
                  ? errors.bgCodes.message
                  : t('page:workflowCases.serverError.validation')}
              </FieldErrorText>
            )}
          </FieldRoot>
        )}

        {/* 対象日 (spec workflow.md L384-390): 1 行 = 1 日付の compact date picker
            行 + 「日付を追加」 (design `demand/cases.html:535-539` [UX 2026-07-27]
            準拠、design feedback 2026-08-13 で旧 popover カレンダーから移行)。 */}
        <FieldRoot gap={1} invalid={errors.targetDates !== undefined} required>
          <FieldLabel textStyle="label">
            {t('page:workflowCases.form.targetDates')}
            <RequiredMark />
          </FieldLabel>
          {/* SoT は RHF `targetDates` (ISO)。 ISO ⇔ Date 変換は component 境界で行う
              (design doc §6 Layer 間接続点)。 */}
          <TargetDateFields
            // 特異日の注記は作成先エリアだけ。 BG 必須時は選択 BG のエリア
            // (エリア欄は BG 候補のフィルタで、作成先そのものではない)。
            areaCodes={singularNoteAreaCodes}
            onChange={(nextDates) => {
              // 「作成」 を押した後は変更のたびに検証し、 0 件のエラーを日付を足した時点で消す。
              setValue('targetDates', nextDates.map(formatDateValue), {
                shouldValidate: isSubmitted,
              })
            }}
            value={targetDateObjects}
          />
          {errors.targetDates !== undefined && (
            <FieldErrorText fontSize="sm">
              {typeof errors.targetDates.message === 'string'
                ? errors.targetDates.message
                : t('page:workflowCases.serverError.validation')}
            </FieldErrorText>
          )}
        </FieldRoot>

        {/* 作成総数プレビュー (AC-WFW-003)。
            **N >= 1 が確定した時だけ、対象日入力の直下に出す。**
            N は BG × 対象日 の結果なので、最後に決まる対象項目のすぐ下で返す。
            未選択時の「選択すると件数が表示されます」 は入力欄を見れば分かることの
            繰り返しで情報量が無いため出さない。 排他制御の事前警告も撤去した —
            実際に衝突した時は AC-WFW-003a が衝突箇所を全件列挙するので、
            常駐の静的警告が並ぶと「今まさに起きた警告」 の重みが下がる。 */}
        {createCount > 0 && (
          <Alert
            status="info"
            title={t(
              requiresBgCode
                ? 'page:workflowCases.create.previewCount'
                : 'page:workflowCases.create.previewCountNoBg',
              { count: createCount },
            )}
          />
        )}

        {/* AC-TAG-002: タグ（雛形の default_tags をプリフィル、0 個ならタグ 0 個。ユーザーはさらに編集可能） */}
        <FieldRoot gap={1}>
          <FieldLabel textStyle="label">
            {t('page:workflowCases.form.tags')}
          </FieldLabel>
          <Box width="100%">
            <WorkflowCaseTagEditor
              onChange={(next) => {
                tagsAutoManagedRef.current = false
                setTags(next)
              }}
              onRemovedTagLabelsChange={setRemovedTagLabels}
              removedTagLabels={removedTagLabels}
              suggestedTags={tagPoolData?.entries}
              tags={tags}
            />
          </Box>
        </FieldRoot>

        {/* 排他衝突エラー (AC-003a) */}
        {errors.root?.serverError && (
          <Alert
            whiteSpace="pre-line"
            description={
              serverErrorRestLines.length > 0
                ? serverErrorRestLines.join('\n')
                : undefined
            }
            status="error"
            title={serverErrorTitle}
          />
        )}
      </VStack>
    </Modal>
  )
}
